import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Clock, Linkedin, MessageCircle, CheckCircle } from "lucide-react";

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } };

export default function ContactPage() {
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", subject: "", message: "" });

  const handleSubmit = (e) => { e.preventDefault(); setSubmitted(true); };

  return (
    <div>
      {/* Hero */}
      <section className="bg-cream pt-28 pb-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="font-playfair text-navy text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
            Get in Touch
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="font-dm text-navy/60 text-lg">
            We respond within 24 hours on business days
          </motion.p>
        </div>
      </section>

      {/* Contact Form + Info */}
      <section className="bg-white py-16">
        <div className="max-w-5xl mx-auto px-4 grid md:grid-cols-2 gap-12">
          {/* Left */}
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <h2 className="font-playfair text-navy text-2xl font-semibold mb-8">Let's talk</h2>
            <div className="space-y-5">
              {[
                { icon: Mail, text: "hello@roleiq.pro", href: "mailto:hello@roleiq.pro" },
                { icon: Clock, text: "Within 24 hours on business days" },
                { icon: Linkedin, text: "Connect on LinkedIn", href: "#" },
                { icon: MessageCircle, text: "Chat on WhatsApp", href: "https://wa.me/919136262913?text=Hi%20RoleIQ%20Team%2C%20I%20would%20like%20to%20know%20more%20about%20your%20platform." },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3">
                  <item.icon className="w-5 h-5 text-gold flex-shrink-0" />
                  {item.href ? (
                    <a href={item.href} target="_blank" rel="noreferrer" className="font-dm text-navy/80 text-sm hover:text-gold transition-colors">{item.text}</a>
                  ) : (
                    <span className="font-dm text-navy/80 text-sm">{item.text}</span>
                  )}
                </div>
              ))}
            </div>
            <p className="font-dm text-gold italic text-sm mt-10">
              &ldquo;Every great team starts with clarity about what great looks like.&rdquo;
            </p>
          </motion.div>

          {/* Right */}
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} transition={{ delay: 0.15 }}>
            {submitted ? (
              <div className="bg-white rounded-xl shadow-lg p-8 text-center">
                <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
                <p className="font-dm text-navy font-semibold text-lg">Thank you — we will be in touch within 24 hours</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-lg p-8 space-y-4">
                <input required placeholder="Full Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full border border-navy/20 rounded-lg px-4 py-3 font-dm text-sm focus:border-gold focus:outline-none" />
                <input required type="email" placeholder="Work Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full border border-navy/20 rounded-lg px-4 py-3 font-dm text-sm focus:border-gold focus:outline-none" />
                <input placeholder="Company Name" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })}
                  className="w-full border border-navy/20 rounded-lg px-4 py-3 font-dm text-sm focus:border-gold focus:outline-none" />
                <input placeholder="Phone Number (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="w-full border border-navy/20 rounded-lg px-4 py-3 font-dm text-sm focus:border-gold focus:outline-none" />
                <select required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })}
                  className="w-full border border-navy/20 rounded-lg px-4 py-3 font-dm text-sm focus:border-gold focus:outline-none text-navy/60">
                  <option value="">Select Subject</option>
                  {["General Inquiry", "Book a Demo", "Enterprise Sales", "Partnership Opportunity", "Press and Media", "Support"].map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
                <textarea required placeholder="Message" rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="w-full border border-navy/20 rounded-lg px-4 py-3 font-dm text-sm focus:border-gold focus:outline-none resize-none" />
                <button type="submit" className="w-full bg-navy text-white font-dm font-semibold py-3 rounded-lg hover:bg-gold hover:text-navy transition-colors">
                  Send Message
                </button>
              </form>
            )}
          </motion.div>
        </div>
      </section>

      {/* Book a Demo */}
      <section className="bg-navy py-20" id="demo">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-playfair text-white text-3xl font-bold mb-4">See RoleIQ in Action</h2>
          <p className="font-dm text-white/70 text-sm max-w-xl mx-auto mb-8">
            Book a free 30-minute live demo. We will walk you through the tool, answer your questions, and show you how RoleIQ works for your specific industry and roles.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <a href="#" className="bg-gold text-navy font-dm font-bold px-8 py-3 rounded-lg hover:bg-gold/90 transition-colors">Book a Demo</a>
            <a href="https://wa.me/919136262913?text=Hi%20RoleIQ%20Team%2C%20I%20would%20like%20to%20know%20more%20about%20your%20platform." target="_blank" rel="noreferrer"
              className="border border-white text-white font-dm px-8 py-3 rounded-lg hover:bg-white/10 transition-colors">Chat on WhatsApp</a>
          </div>
          <div className="flex flex-wrap justify-center gap-6">
            {["Live tool walkthrough", "Custom use case discussion", "No sales pressure \u2014 just a real conversation"].map((t) => (
              <div key={t} className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-gold" />
                <span className="font-dm text-white/70 text-sm">{t}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
